import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import "./module.scss";

const orderStatuses = ["All Status", "Pending", "Paid", "Collected", "Cancelled", "Refunded"];

const orders = [
  ["SB-10084", "Channie / 012-3456789", "Pending", "Today, 6:30 PM", "RM18.90"],
  ["SB-10083", "Daniel / 011-2233445", "Paid", "Today, 7:00 PM", "RM12.50"],
  ["SB-10082", "Mei Ling / 016-9988776", "Collected", "Today, 5:30 PM", "RM9.90"],
  ["SB-10081", "Aiman / 019-7788990", "Cancelled", "Today, 6:00 PM", "RM15.00"],
  ["SB-10080", "Sarah / 012-1112223", "Refunded", "Yesterday, 6:30 PM", "RM21.00"],
];

const reportRows = [
  ["Sales Report", "Revenue summary by selected date range.", "Today", "28", "RM682.40"],
  ["Orders Report", "Order activity, cancellations, and refunds.", "Today", "28", "RM682.40"],
  ["Product Report", "Daily quantity, sold count, and sold out tracking.", "Today", "4", "RM102.30"],
];

const reportDetails = {
  "Sales Report": {
    title: "Sales Report",
    summary: [
      ["Total Revenue", "RM682.40", "+12.8% vs yesterday"],
      ["Total Orders", "28", "+6 vs yesterday"],
      ["Average Order", "RM24.37", "Today"],
    ],
    columns: ["Time", "Order No", "Customer", "Amount"],
    rows: [
      ["5:30 PM", "SB-10082", "Mei Ling", "RM9.90"],
      ["6:30 PM", "SB-10084", "Channie", "RM18.90"],
      ["7:00 PM", "SB-10083", "Daniel", "RM12.50"],
    ],
  },
  "Orders Report": {
    title: "Orders Report",
    summary: [
      ["Total Orders", "28", "Today"],
      ["Collected", "18", "64% completed"],
      ["Cancelled / Refunded", "3", "Requires review"],
    ],
    columns: ["Status", "Orders", "Amount", "Remarks"],
    rows: [
      ["Pending", "4", "RM76.60", "Waiting payment"],
      ["Paid", "6", "RM148.20", "Waiting pickup"],
      ["Collected", "18", "RM457.60", "Completed"],
      ["Cancelled", "2", "RM30.00", "Not collected"],
      ["Refunded", "1", "RM21.00", "Admin processed"],
    ],
  },
  "Product Report": {
    title: "Product Report",
    summary: [
      ["Daily Quantity", "50", "Reset per product"],
      ["Daily Sold", "18", "36% sold"],
      ["Remaining", "32", "Available for pickup"],
    ],
    columns: ["Product", "Daily Quantity", "Daily Sold", "Remaining"],
    rows: [["Chicken Rice Bowl", "50", "18", "32"]],
  },
};

const settingRows = [
  ["Email Notification", "Vendor order email notification settings.", "Enabled", "Enabled"],
  ["Password", "Change vendor portal password.", "Updated", "Enabled"],
];

const pageConfig = {
  orders: {
    title: "Orders",
    subtitle: "Monitor guest checkout orders, payment status, and pickup progress.",
    action: "Export Orders",
  },
  reports: {
    title: "Reports",
    subtitle: "View outlet sales and order performance reports.",
    action: "Export Report",
  },
  settings: {
    title: "Settings",
    subtitle: "Manage vendor notification and account password settings.",
    action: null,
  },
};

function Hero({ config }) {
  return (
    <section className="module-hero">
      <div>
        <p>SecondBite Vendor</p>
        <h2>{config.title}</h2>
        <span>{config.subtitle}</span>
      </div>
      {config.action && <button type="button">{config.action}</button>}
    </section>
  );
}

const ModulePage = ({ type = "orders" }) => {
  const navigate = useNavigate();
  const config = pageConfig[type] || pageConfig.orders;
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("All Status");
  const [editingSetting, setEditingSetting] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const filteredOrders = useMemo(() => {
    return orders.filter((row) => {
      const matchStatus = status === "All Status" || row[2] === status;
      const matchKeyword = `${row[0]} ${row[1]}`.toLowerCase().includes(keyword.toLowerCase());
      return matchStatus && matchKeyword;
    });
  }, [keyword, status]);

  return (
    <div className="module-page">
      <Hero config={config} />

      {type === "orders" && (
        <>
          <section className="module-filters module-filters--three">
            <DateRangePicker label="Date Range" />
            <label>
              <span>Order Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {orderStatuses.map((item) => <option key={item}>{item}</option>)}
              </select>
            </label>
            <label>
              <span>Keyword</span>
              <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="Search order or customer..." />
            </label>
          </section>

          <section className="module-card">
            <div className="module-card__header">
              <h3>Orders Listing</h3>
              <p>Static MVP UI sample. Connect API later.</p>
            </div>
            <div className="module-table module-table--orders">
              <div className="module-table__head">
                <span>Order No</span><span>Customer</span><span>Status</span><span>Pickup Time</span><span>Amount</span><span>Actions</span>
              </div>
              {filteredOrders.map((row) => (
                <button className="module-table__row" type="button" key={row[0]} onClick={() => navigate(`/vendor/orders/${row[0]}`)}>
                  {row.map((cell, i) => (
                    <span key={i} className={i === 2 ? `status-pill status-pill--${cell.toLowerCase()}` : ""}>{cell}</span>
                  ))}
                  <span>View</span>
                </button>
              ))}
            </div>
          </section>
        </>
      )}

      {type === "reports" && (
        <>
          <section className="module-filters module-filters--three">
            <div className="module-filter-field">
              <span>Date Range</span>
              <DateRangePicker />
            </div>
            <label>
              <span>Report Type</span>
              <select>
                <option>All Reports</option>
                <option>Sales Report</option>
                <option>Orders Report</option>
                <option>Product Report</option>
              </select>
            </label>
            <label>
              <span>Keyword</span>
              <input placeholder="Search report..." />
            </label>
          </section>

          <section className="module-card">
            <div className="module-card__header"><h3>Reports Listing</h3></div>
            <div className="module-table module-table--reports">
              <div className="module-table__head">
                <span>Report</span><span>Description</span><span>Date Range</span><span>Total Orders</span><span>Total Revenue</span><span>Actions</span>
              </div>
              {reportRows.map((row) => (
                <div className="module-table__row" key={row[0]}>
                  {row.map((cell) => <span key={cell}>{cell}</span>)}
                  <button type="button" onClick={() => setSelectedReport(row[0])}>View</button>
                </div>
              ))}
            </div>
          </section>

          {selectedReport && (
            <div className="module-modal-backdrop" role="presentation">
              <section className="module-modal module-modal--report" role="dialog" aria-modal="true" aria-label={reportDetails[selectedReport].title}>
                <div className="module-modal__header">
                  <div>
                    <h3>{reportDetails[selectedReport].title}</h3>
                    <p>Today · Hikari House</p>
                  </div>
                  <button type="button" className="module-modal__close" onClick={() => setSelectedReport(null)}>×</button>
                </div>

                <div className="module-modal__body">
                  <div className="report-summary-grid">
                    {reportDetails[selectedReport].summary.map((item) => (
                      <div className="report-summary-card" key={item[0]}>
                        <span>{item[0]}</span>
                        <strong>{item[1]}</strong>
                        <small>{item[2]}</small>
                      </div>
                    ))}
                  </div>

                  <div className="report-detail-table">
                    <div className="report-detail-table__head" style={{ gridTemplateColumns: `repeat(${reportDetails[selectedReport].columns.length}, minmax(0, 1fr))` }}>
                      {reportDetails[selectedReport].columns.map((column) => <span key={column}>{column}</span>)}
                    </div>
                    {reportDetails[selectedReport].rows.map((row, index) => (
                      <div className="report-detail-table__row" key={index} style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                        {row.map((cell) => <span key={cell}>{cell}</span>)}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="module-modal__footer">
                  <button type="button" className="module-modal__secondary" onClick={() => setSelectedReport(null)}>Close</button>
                  <button type="button" onClick={() => setSelectedReport(null)}>Export Report</button>
                </div>
              </section>
            </div>
          )}
        </>
      )}

      {type === "settings" && (
        <>
          <section className="module-card">
            <div className="module-card__header">
              <h3>Settings Listing</h3>
              <p>Only vendor account settings are editable here. Outlet, bank, and checkout settings are managed by admin.</p>
            </div>
            <div className="module-table module-table--settings">
              <div className="module-table__head">
                <span>Setting</span><span>Description</span><span>Value</span><span>Status</span><span>Actions</span>
              </div>
              {settingRows.map((row) => (
                <div className="module-table__row" key={row[0]}>
                  {row.map((cell, i) => (
                    <span key={i} className={i === 3 ? "status-pill status-pill--active" : ""}>{cell}</span>
                  ))}
                  <button type="button" onClick={() => setEditingSetting(row[0])}>Edit</button>
                </div>
              ))}
            </div>
          </section>

          {editingSetting && (
            <div className="module-modal-backdrop" role="presentation">
              <section className="module-modal" role="dialog" aria-modal="true" aria-label={`Edit ${editingSetting}`}>
                <div className="module-modal__header">
                  <div>
                    <h3>Edit {editingSetting}</h3>
                    <p>Static MVP form. Connect API later.</p>
                  </div>
                  <button type="button" className="module-modal__close" onClick={() => setEditingSetting(null)}>×</button>
                </div>

                <div className="module-modal__body">
                  {editingSetting === "Email Notification" && (
                    <div className="module-form-grid module-form-grid--settings">
                      <label>
                        <span>Receive New Order Email</span>
                        <select defaultValue="Yes"><option>Yes</option><option>No</option></select>
                      </label>
                      <label>
                        <span>Notification Email</span>
                        <input defaultValue="hikari@example.com" />
                      </label>
                      <label className="module-form-grid__full">
                        <span>Daily Summary Email</span>
                        <select defaultValue="Enabled"><option>Enabled</option><option>Disabled</option></select>
                      </label>
                    </div>
                  )}

                  {editingSetting === "Password" && (
                    <div className="module-form-grid module-form-grid--settings module-form-grid--password">
                      <label className="module-form-grid__full">
                        <span>Current Password</span>
                        <input type="password" placeholder="Enter current password" />
                      </label>
                      <label>
                        <span>New Password</span>
                        <input type="password" placeholder="Enter new password" />
                      </label>
                      <label>
                        <span>Confirm New Password</span>
                        <input type="password" placeholder="Confirm new password" />
                      </label>
                    </div>
                  )}
                </div>

                <div className="module-modal__footer">
                  <button type="button" className="module-modal__secondary" onClick={() => setEditingSetting(null)}>Cancel</button>
                  <button type="button" onClick={() => setEditingSetting(null)}>Save Changes</button>
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ModulePage;
